import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { AppBar } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
// ?react suffix is necessary because you're rendering the Logo as a component, not as an image URL.
// This is how vite-plugin-svgr knows to convert the SVG markup into JSX that you can use directly.
import Logo from '../../../assets/images/airportliner_logo.svg?react';
import de from '../../../assets/images/de.png';
import uk from '../../../assets/images/uk.png';
import { useLoyalty } from '../../../loyalty/LoyaltyContext';
import { getTierStyle } from '../../../loyalty/tiers';
import css from './Header.module.css';

const MENU_ID = 'headerMainMenu';

/** Endonyms — deliberately not translated, a language switcher names itself in its own language. */
const LANGUAGE_NAMES = { de: 'Deutsch', en: 'English' };

/** Off-screen but readable by assistive tech (same recipe as @mui/utils' visuallyHidden). */
const srOnly = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

/** The "NEU" pill as it looks inside the (light) drawer. */
const drawerBadgeSx = {
    flexShrink: 0,
    ml: 1,
    px: 0.75,
    py: '1px',
    borderRadius: '999px',
    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
    color: 'primary.main',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.6,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
};

const Header = ({ onLogin, onLogout, onSignup, user }) => {
    const { i18n, t } = useTranslation();
    const theme = useTheme();
    const { tierName, eligible } = useLoyalty();
    // Below `md` (900px) the centred nav, the user zone and the flags no longer fit on one row.
    // Between `md` and `lg` the wide bar survives in a condensed form (see Header.module.css) so
    // that 1024px laptops and small desktops keep the full desktop header rather than a mobile menu.
    // `noSsr` makes the very first render already correct, otherwise phones paint the wide bar once.
    const isCompact = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
    const [menuOpen, setMenuOpen] = useState(false);
    const { pathname } = useLocation();

    // Close the drawer whenever the route changes or we grow back to the wide layout.
    useEffect(() => setMenuOpen(false), [pathname]);
    useEffect(() => {
        if (!isCompact) setMenuOpen(false);
    }, [isCompact]);

    const navItems = buildNavItems({ t, user, loyaltyEligible: eligible });
    const tierStyle = getTierStyle(tierName);

    const tierChip = tierName ? (
        <Chip
            size="small"
            icon={<WorkspacePremiumIcon />}
            label={
                <>
                    {/* "GOLD" on its own is ambiguous out of context — name it for screen readers. */}
                    <Box component="span" sx={srOnly}>{`${t('loyalty.hero.status')}: `}</Box>
                    {t(`loyalty.tier.${tierName}`)}
                </>
            }
            sx={{
                flexShrink: 0,
                height: 24,
                borderRadius: '999px',
                backgroundColor: tierStyle.bgcolor,
                color: tierStyle.color,
                '& .MuiChip-label': {
                    px: 0.75,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                },
                '& .MuiChip-icon': {
                    ml: '6px',
                    mr: '-2px',
                    fontSize: 16,
                    color: 'inherit',
                },
            }}
        />
    ) : null;

    return (
        <AppBar position="static">
            <div className={css.host}>
                <div className={css.leftZone}>
                    {/*in case we want to add a target=_blank, we need to consider this:
                    https://www.jitbit.com/alexblog/256-targetblank---the-most-underestimated-vulnerability-ever/*/}
                    <a href={getLinkToAirportLinerHomepage(i18n)} className={css.logoLink}>
                        <Logo className={css.logo} title={t('appTitle')} />
                    </a>
                </div>

                {!isCompact && navItems.length > 0 && (
                    <nav className={css.centerZone} aria-label={t('appTitle')}>
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) =>
                                    isActive ? `${css.navLink} ${css.navLinkActive}` : css.navLink
                                }
                                data-cy={item.dataCy}
                            >
                                <span className={css.navLabel}>{item.label}</span>
                                {item.badge && <span className={css.newBadge}>{item.badge}</span>}
                            </NavLink>
                        ))}
                    </nav>
                )}

                <div className={css.rightZone}>
                    {isCompact ? (
                        <>
                            {/* Logged out, login + signup are quick actions in the bar itself (not in the
                                drawer), so a phone visitor sees them without opening the menu. Login is
                                icon-only: both labels do not fit beside the logo at 360px. */}
                            {!user && (
                                <>
                                    <IconButton
                                        onClick={onLogin}
                                        color="inherit"
                                        aria-label={t('button.login')}
                                        data-cy="loginButton"
                                    >
                                        <AccountCircleIcon />
                                    </IconButton>
                                    <Button
                                        onClick={onSignup}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            flexShrink: 0,
                                            px: 1,
                                            color: 'primary.contrastText',
                                            borderColor: 'primary.contrastText',
                                            textTransform: 'none',
                                            fontSize: 13,
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                        data-cy="signupButton"
                                    >
                                        {t('button.signup')}
                                    </Button>
                                </>
                            )}
                            <IconButton
                                color="inherit"
                                edge="end"
                                onClick={() => setMenuOpen(true)}
                                aria-label={t('nav.openMenu')}
                                aria-expanded={menuOpen}
                                aria-controls={MENU_ID}
                                data-cy="menuButton"
                            >
                                <MenuIcon />
                            </IconButton>
                        </>
                    ) : (
                        <>
                            {user ? (
                                <>
                                    <Button
                                        component={Link}
                                        to="/profile"
                                        sx={{
                                            minWidth: 0,
                                            // Shrinks first in the condensed band so the nav stays centred.
                                            maxWidth: { xs: '180px', lg: '260px' },
                                            display: 'block',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'left',
                                            color: 'primary.contrastText',
                                            textTransform: 'none',
                                            fontSize: 15,
                                            fontWeight: 500,
                                        }}
                                        data-cy="profileButton"
                                    >
                                        {`${user.firstName} ${user.lastName}`}
                                    </Button>
                                    {tierChip}
                                    <IconButton
                                        onClick={onLogout}
                                        color="inherit"
                                        aria-label={t('nav.logout')}
                                        sx={{ p: { xs: 0.5, lg: 1 } }}
                                        data-cy="logoutButton"
                                    >
                                        <LogoutIcon fontSize="small" />
                                    </IconButton>
                                </>
                            ) : (
                                <>
                                    <Button
                                        onClick={onLogin}
                                        sx={{
                                            color: 'primary.contrastText',
                                            textTransform: 'none',
                                            fontSize: 15,
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                        data-cy="loginButton"
                                        endIcon={<AccountCircleIcon />}
                                    >
                                        {t('button.login')}
                                    </Button>
                                    <Button
                                        onClick={onSignup}
                                        variant="outlined"
                                        sx={{
                                            color: 'primary.contrastText',
                                            borderColor: 'primary.contrastText',
                                            textTransform: 'none',
                                            fontSize: 15,
                                            fontWeight: 500,
                                            whiteSpace: 'nowrap',
                                        }}
                                        data-cy="signupButton"
                                    >
                                        {t('button.signup')}
                                    </Button>
                                </>
                            )}
                            <LangItem lng="de" src={de} />
                            <LangItem lng="en" src={uk} />
                        </>
                    )}
                </div>
            </div>

            {/* Only mounted in the compact layout — that is what keeps every `data-cy` unique per viewport.
                `keepMounted` keeps the hamburger's `aria-controls` target in the DOM while the menu is shut. */}
            {isCompact && (
                <Drawer
                    anchor="right"
                    open={menuOpen}
                    onClose={() => setMenuOpen(false)}
                    ModalProps={{ keepMounted: true }}
                >
                    <Box id={MENU_ID} role="presentation" sx={{ width: 280, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
                            <IconButton onClick={() => setMenuOpen(false)} aria-label={t('nav.closeMenu')} edge="end">
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Logged out, login + signup live in the bar (see above) — the drawer only
                            holds the language switch then. */}
                        {user && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    alignItems: 'flex-start',
                                    px: 2,
                                }}
                            >
                                <Button
                                    component={Link}
                                    to="/profile"
                                    onClick={() => setMenuOpen(false)}
                                    sx={{
                                        minWidth: 0,
                                        maxWidth: '100%',
                                        px: 0,
                                        display: 'block',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        textAlign: 'left',
                                        color: 'primary.main',
                                        textTransform: 'none',
                                        fontSize: 16,
                                        fontWeight: 600,
                                    }}
                                    data-cy="profileButton"
                                >
                                    {`${user.firstName} ${user.lastName}`}
                                </Button>
                                {tierChip}
                            </Box>
                        )}

                        <Divider sx={{ mt: 2 }} />

                        {/* Logged out there are no nav entries and no logout, so the list is skipped
                            entirely rather than rendered as an empty <ul> between two dividers. */}
                        {user && (
                            <>
                                <List component="nav" aria-label={t('appTitle')}>
                                    {navItems.map((item) => (
                                        <ListItemButton
                                            key={item.to}
                                            component={Link}
                                            to={item.to}
                                            selected={isRouteActive(pathname, item.to)}
                                            onClick={() => setMenuOpen(false)}
                                            data-cy={item.dataCy}
                                            sx={{ color: 'text.primary', textDecoration: 'none' }}
                                        >
                                            <ListItemText primaryTypographyProps={{ fontSize: 15, fontWeight: 500 }}>
                                                {item.label}
                                            </ListItemText>
                                            {item.badge && (
                                                <Box component="span" sx={drawerBadgeSx}>
                                                    {item.badge}
                                                </Box>
                                            )}
                                        </ListItemButton>
                                    ))}
                                    <ListItemButton
                                        onClick={() => {
                                            setMenuOpen(false);
                                            onLogout();
                                        }}
                                        data-cy="logoutButton"
                                    >
                                        <ListItemText primaryTypographyProps={{ fontSize: 15, fontWeight: 500 }}>
                                            {t('nav.logout')}
                                        </ListItemText>
                                        <LogoutIcon fontSize="small" />
                                    </ListItemButton>
                                </List>

                                <Divider />
                            </>
                        )}

                        {/* A plain Box, not a ListItem — this row sits outside the <ul> above. */}
                        <Box sx={{ display: 'flex', gap: 1, px: 1, py: 1 }}>
                            <LangItem lng="de" src={de} onSelect={() => setMenuOpen(false)} />
                            <LangItem lng="en" src={uk} onSelect={() => setMenuOpen(false)} />
                        </Box>
                    </Box>
                </Drawer>
            )}
        </AppBar>
    );
};

/**
 * The centre nav entries, in display order.
 * Logged out there is no nav at all — /profile and /loyalty redirect to /booking anyway, and
 * /booking is where a logged-out visitor already is.
 */
const buildNavItems = ({ t, user, loyaltyEligible }) => {
    if (!user) return [];

    const items = [
        { to: '/booking', label: t('nav.newBooking'), dataCy: 'newBookingButton' },
        { to: '/profile', label: t('nav.myBookings'), dataCy: 'myBookingsButton' },
    ];

    if (loyaltyEligible) {
        items.push({
            to: '/loyalty',
            label: t('loyalty.nav'),
            badge: t('loyalty.badgeNew'),
            dataCy: 'loyaltyButton',
        });
    }

    return items;
};

const isRouteActive = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`);

/**
 * Put together the correct link to the airportLiner homepage.
 * Take the language object and convert the language setting to the proper string needed for the website link.
 */
const getLinkToAirportLinerHomepage = (i18n) => {
    let linkLanguage;
    let languageFromI18N = i18n.language.split('-');

    if (languageFromI18N[0] === 'de') {
        linkLanguage = '';
    } else linkLanguage = 'en';

    return `https://www.airportliner.com/${linkLanguage}`;
};

const LangItem = ({ lng, src, onSelect }) => {
    const { i18n } = useTranslation();
    const isCurrent = i18n.language.split('-')[0] === lng;

    return (
        <IconButton
            color="inherit"
            onClick={() => {
                i18n.changeLanguage(lng);
                if (onSelect) onSelect();
            }}
            // The flag alone carries no accessible name, and the active language is only shown visually.
            aria-label={LANGUAGE_NAMES[lng]}
            aria-pressed={isCurrent}
            // Tighter in the condensed band (md–lg) so the centred nav keeps its room.
            sx={{ p: { md: 0.5, lg: 1 } }}
        >
            <img src={src} alt="" width="24" height="24" />
        </IconButton>
    );
};

export { Header };
